import React, { useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import type { Product } from "@/features/dmManagement/types";
import { DESIGN_PDF_MAX_FILE_SIZE } from "@/features/dmManagement/types/constants";

interface ComponentFileUploadProps {
  label: string;
  seq: number;
  products: Product[];
  selectedProductId?: number;
  onProductChange: (productId: number) => void;
  onFileChange: (file: File | null) => void;
  error?: string;
}

/**
 * デザイン構成ファイルアップロード
 * 商品選択とPDFファイルのアップロードを行う入力コンポーネント
 */
export const ComponentFileUpload: React.FC<ComponentFileUploadProps> = ({
  label,
  seq,
  products,
  selectedProductId,
  onProductChange,
  onFileChange,
  error,
}) => {
  const [sizeError, setSizeError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > DESIGN_PDF_MAX_FILE_SIZE) {
      setSizeError("ファイルサイズは2GB以下にしてください");
      onFileChange(null);
      return;
    }
    setSizeError(null);
    onFileChange(file);
  };

  const handleProductSelect = (e: SelectChangeEvent): void => {
    onProductChange(Number(e.target.value));
  };

  const isFixed = products.length <= 1;
  const fixedProduct = isFixed ? products[0] : undefined;

  return (
    <Box sx={{ mb: 2 }}>
      {isFixed && fixedProduct && (
        <TextField
          size="small"
          label="商品"
          value={fixedProduct.name}
          InputProps={{ readOnly: true }}
          sx={{ mb: 1, minWidth: 300 }}
        />
      )}
      {!isFixed && products.length > 1 && (
        <FormControl size="small" sx={{ mb: 1, minWidth: 300 }}>
          <InputLabel id={`product-select-label-${seq}`}>商品</InputLabel>
          <Select
            labelId={`product-select-label-${seq}`}
            value={selectedProductId != null ? String(selectedProductId) : ""}
            label="商品"
            onChange={handleProductSelect}
          >
            {products.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Box>
        <input
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          aria-label={label}
        />
        {(error ?? sizeError) && (
          <FormHelperText error>{error ?? sizeError}</FormHelperText>
        )}
      </Box>
    </Box>
  );
};
